import { useCallback, type ReactElement } from 'react';
import { Link, useOutletContext } from 'react-router';
import { useListePaginee } from '../coquille/useChargement.js';
import { CHEMINS } from '../routes.js';

interface Espace { readonly code: string; readonly nom: string; readonly role: string }

interface LigneProjet {
  readonly code: string;
  readonly nom: string;
  readonly description: string | null;
}

interface ReponseListe { readonly projets: readonly LigneProjet[] }

/**
 * LA LISTE DES PROJETS D UN ESPACE [contrat 28-BQ].
 *
 * Le niveau CLIENT mene au niveau PROJET, et c est le seul chemin : une
 * automatisation appartient a un projet, donc on ne l atteint qu en passant
 * par lui. Melanger les deux niveaux dans une meme barre reconstruirait la
 * confusion que 28-BH a supprimee du modele.
 *
 * PAS DE PAGINATION : la route n en rend pas. L extracteur annonce donc un
 * curseur nul, ce qui est une INFORMATION, pas un contournement -- le jour
 * ou la route paginera, c est ici qu on lira le curseur.
 */
export function Projets(): ReactElement {
  const espace = useOutletContext<Espace>();

  const extraire = useCallback((r: ReponseListe) => ({
    lignes: r.projets, curseur: null,
  }), []);

  const { etat } = useListePaginee<ReponseListe, LigneProjet>(
    `/locataires/${encodeURIComponent(espace.code)}/projets`, extraire);

  if (etat.phase === 'chargement') {
    return <p className="etat-vide">Chargement des projets…</p>;
  }
  if (etat.phase === 'echec') {
    return <p className="etat-vide">Les projets n ont pas pu être chargés.</p>;
  }
  // DEUX AFFORDANCES, UN SEUL GESTE. L etat vide est le cas de sortie de
  // 29-C : un espace neuf ne doit JAMAIS etre une impasse. La formulation
  // differe, la destination est unique.
  if (etat.donnees.lignes.length === 0) {
    return (
      <div className="etat-vide">
        <p>Cet espace n a pas encore de projet.<br />
          Creez votre premier projet pour commencer.</p>
        <p><Link to={CHEMINS.nouveauProjet(espace.code)} className="bouton">
          + Creer le premier projet</Link></p>
      </div>
    );
  }
  return (
    <>
      <p className="contenu__entete">
        <Link to={CHEMINS.nouveauProjet(espace.code)} className="bouton bouton--discret">
          + Nouveau projet</Link>
      </p>
    <ul className="liste-cartes">
      {etat.donnees.lignes.map((p) => (
        <li key={p.code}>
          <Link to={CHEMINS.projet(espace.code, p.code)} className="carte">
            <span className="carte__titre">{p.nom}</span>
            {p.description !== null && (
              <span className="carte__detail">{p.description}</span>
            )}
          </Link>
        </li>
      ))}
    </ul>
    </>
  );
}
